import axios, { AxiosResponse } from "axios";

import { SamskipAPIResponse, DomesticTypes } from "@assets/types";

const SamskipAPI = process.env.SamskipAPI;

class DomesticService {
  getPostCodes = async () => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticPostCode[]>
    > = await axios.get(`${SamskipAPI}/domestic/postcodes`);
    return res.data.ReturnItem.Data;
  };

  getPostCode = async (postCode: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticPostCode>
    > = await axios.get(
      `${SamskipAPI}/domestic/postcodes/${encodeURIComponent(postCode)}`
    );
    return res.data.ReturnItem.Data;
  };

  getDepartments = async (companySSN: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticDepartment[]>
    > = await axios.get(
      `${SamskipAPI}/domestic/addresses/${encodeURIComponent(
        companySSN
      )}/departments`
    );
    return res.data.ReturnItem.Data;
  };

  getItemConditions = async () => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticItemCondition[]>
    > = await axios.get(`${SamskipAPI}/domestic/itemconditions`);
    return res.data.ReturnItem.Data;
  };

  getSelectedCustomer = async () => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticCustomer>
    > = await axios.get(
      `${SamskipAPI}/domestic/customers/companies/selected-company`,
      {
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );
    return res.data.ReturnItem.Data;
  };

  searchCompanyCustomers = async (companySearchString: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticCustomer[]>
    > = await axios.get(`${SamskipAPI}/domestic/customers/companies/search`, {
      params: {
        searchString: companySearchString,
      },
      withCredentials: true,
    });
    return res.data.ReturnItem.Data;
  };

  searchPersonCustomers = async (personSSN: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticCustomer[]>
    > = await axios.get(
      `${SamskipAPI}/domestic/customers/person-registry/search`,
      {
        params: {
          searchString: personSSN,
        },
        withCredentials: true,
      }
    );
    return res.data.ReturnItem.Data;
  };

  validateSSN = async (personSSN: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticPersonValidation>
    > = await axios.post(
      `${SamskipAPI}/domestic/customers/person-registry/validate`,
      personSSN,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return res.data.ReturnItem.Data.IsValid;
  };

  searchHazardousMaterial = async (materialSearchString: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticHazardousMaterial[]>
    > = await axios.get(`${SamskipAPI}/domestic/dangerous/search`, {
      params: {
        searchString: materialSearchString,
      },
      withCredentials: true,
    });
    return res.data.ReturnItem.Data;
  };

  createBooking = async (booking: DomesticTypes.DomesticExposedBooking) => {
    const res: AxiosResponse<SamskipAPIResponse<string>> = await axios.post(
      `${SamskipAPI}/domestic/bookings/create`,
      booking
    );
    return res.data.ReturnItem.Data;
  };

  getBooking = async (bookingNumber: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticExposedBooking>
    > = await axios.get(`${SamskipAPI}/domestic/bookings/${bookingNumber}`, {
      withCredentials: true,
    });
    return res.data.ReturnItem.Data;
  };

  getBookingLabelInfo = async (bookingNumber: string) => {
    const res: AxiosResponse<
      SamskipAPIResponse<DomesticTypes.DomesticBookingLabel>
    > = await axios.get(
      `${SamskipAPI}/domestic/bookings/${bookingNumber}/label`
    );
    return res.data.ReturnItem.Data;
  };

  areChristmasPackagesAvailable = async () => {
    const res: AxiosResponse<boolean> = await axios.get(
      `${SamskipAPI}/domestic/christmas-packages`
    );
    return res.data;
  };
}

export default new DomesticService();
